<script setup lang="ts">
import { api } from '@/api';
import {
  type EquipmentTypeCatalogDto,
  type PtwQaEquipmentWithRegistrationDto,
  QaEquipmentSubCategory,
} from '@/api/generator';
import PtwBackButton from '@/components/buttons/PtwBackButton.vue';
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import Select from '@/components/forms/Select/Select.vue';
import SubmitButton from '@/components/forms/SubmitButton/SubmitButton.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import {
  medicalSystemTypeManufacturer,
  medicalSystemTypeModel,
  medicalSystemTypeSubCategory,
} from '@/components/icons/icons.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import InfoMessage from '@/components/InfoMessage/InfoMessage.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import { SortItem } from '@/components/OverviewTable/types.ts';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { useNotificationStore } from '@/stores/notificationStore.ts';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore.ts';
import { buildOdataQuery } from '@/utils/odata';
import { getTranslationPrefix } from '@/utils/translationPrefix.ts';
import type { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import QaEquipmentAddQuickLinks from '@/views/qaEquipment/components/QaEquipmentAddQuickLinks.vue';
import QaEquipmentRegistration from '@/views/qaEquipment/components/QaEquipmentRegistration.vue';
import {
  getPatchedQuery,
  successfullyRegisteredMsg,
  isRegistrablePtwQaEquipment,
} from '@/views/qaEquipment/utils.ts';
import { useTranslation } from 'i18next-vue';
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import QaEquipmentAddFilterAndSorting from './components/QaEquipmentAddFilterAndSorting.vue';
import { QaEquipmentAddFilterAndSortingProps } from './types';
import { TabDefinition } from '@/components/Tabs/types';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport.ts';

const router = useRouter();
const { t } = useTranslation();

interface AddConfigurationViewProps {
  valuesRegistration?: PtwQaEquipmentWithRegistrationDto;
}

defineProps<AddConfigurationViewProps>();

// Page setup ==============================================================
const tabs = computed(
  () =>
    ({
      ptw: {
        title: t('Ptw'),
        hideContentSheet: true,
      },
      otherManufacturer: {
        title: t('OtherManufacturer'),
        hideContentSheet: true,
      },
    }) satisfies TabDefinition,
);

// PTW equipment ===========================================================
const qaEquipmentStore = useQaEquipmentStore();

const beforeEachHook = router.beforeEach((to, from, next) => {
  if (
    from.name === routenames.qaEquipment.add.configuration &&
    to.name === routenames.qaEquipment.add.fromCatalog
  ) {
    next();
  } else if (
    from.name === routenames.qaEquipment.add.fromCatalog &&
    to.name === routenames.qaEquipment.add.configuration
  ) {
    next();
  } else {
    qaEquipmentStore.setFilterObject({});
    next();
  }
});

const options = ref<EquipmentTypeCatalogDto[]>();
const filterOptions = ref<EquipmentTypeCatalogDto[]>();
const unicheckOption = ref<EquipmentTypeCatalogDto[]>();

const leavingPage = ref<boolean>(false);

const numberOfColumns = 5;
const columnWidth = `${100 / numberOfColumns}%`;
const headers = [
  {
    key: 'mostCommon',
    title: t('MostCommon'),
    hidden: true,
  },
  {
    key: 'model',
    title: t('Model') + ' | ' + t('SubModel'),
    width: columnWidth,
  },
  {
    key: 'brand1',
    title: t('Brand'),
    width: columnWidth,
  },
  {
    key: 'subCategory',
    title: t('Type_one'),
    width: columnWidth,
  },
  {
    key: 'typeNumber',
    title: t('ArticleNumber'),
    width: columnWidth,
  },
  {
    key: 'withSerialNumber',
    title: t('SerialNumber'),
    width: columnWidth,
  },
] as const;

const sortBy = ref<SortItem[]>([
  {
    key: 'mostCommon',
    order: 'desc',
  },
]);

const sortByFromFilter = ref<SortItem[]>([
  {
    key: 'mostCommon',
    order: 'desc',
  },
]);

const sortByItems: QaEquipmentAddFilterAndSortingProps['sortByItems'] = [
  ...headers.map((header) => {
    return {
      title: header.title,
      value: header.key,
    };
  }),
];

const onResetFilters = () => qaEquipmentStore.setFilterObject({});

const {
  action: loadEquipment,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  if (!unicheckOption.value) {
    const { result: unicheckResult } = await api.qaEquipmentApi.getQaEquipmentOptions(
      buildOdataQuery({
        filter: {
          and: [
            {
              typeNumber: { eq: 'T94255' },
            },
          ],
        },
      }),
    );
    if (unicheckResult?.value) {
      unicheckOption.value = unicheckResult.value;
    }
  }
  const { result: optionsResult } = await api.qaEquipmentApi.getQaEquipmentOptions(
    getPatchedQuery(sortBy.value, qaEquipmentStore.addConfigurationFilterObject),
  );
  if (optionsResult?.value) {
    options.value = optionsResult.value;
  }
}, true);

const quickLinkOptions = computed(() => unicheckOption.value);

watchEffect(() => {
  if (!leavingPage.value) loadEquipment();
});

onMounted(async () => {
  const { result } = await api.qaEquipmentApi.getQaEquipmentOptions(
    getPatchedQuery(sortBy.value, {}),
  );
  if (result?.value) {
    filterOptions.value = result.value;
  }
});

const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);

// Registration dialog =====================================================
const notificationStore = useNotificationStore();
qaEquipmentStore.registrationSuccess = () => {
  if (
    qaEquipmentStore.deviceForRegistration &&
    isRegistrablePtwQaEquipment(qaEquipmentStore.deviceForRegistration)
  ) {
    notificationStore.success(successfullyRegisteredMsg(qaEquipmentStore.deviceForRegistration));
    qaEquipmentStore.setDeviceForRegistration(undefined);
  }
};
// Other manufacturer equipment =============================================
const otherQaEquipmentOptions = [
  ...Object.values(QaEquipmentSubCategory).map((subCategory) => ({
    title: t(
      `${getTranslationPrefix(
        'qaEquipmentScope',
        'rtSystemSubCategory',
      )}.${subCategory}` as I18nKey,
    ),
    value: subCategory,
  })),
].sort((a, b) => {
  if (a.value === 'OtherDosimetryQaEquipment') return 1;
  if (b.value === 'OtherDosimetryQaEquipment') return -1;
  return a.value.localeCompare(b.value);
});

const otherQaEquipmentOptionSelected = ref<string>();

const clickRow = (item: EquipmentTypeCatalogDto) => {
  router.push({
    name: routenames.qaEquipment.add.fromCatalog,
    params: { template: item.id },
  });
};

onUnmounted(() => {
  beforeEachHook();
});

watch(
  () => sortByFromFilter.value,
  () => {
    if (sortByFromFilter.value.length > 0) {
      if (sortByFromFilter.value[0].key === 'model') {
        sortBy.value = [
          sortByFromFilter.value[0],
          {
            key: 'submodel',
            order: sortByFromFilter.value[0].order,
          },
        ];
      } else {
        sortBy.value = sortByFromFilter.value;
      }
    }
  },
  {
    deep: true,
  },
);

const handleSortByChange = (sortItems: SortItem[]) => {
  if (sortItems.length > 0) {
    if (sortItems[0].key === 'model') {
      sortBy.value = [
        sortItems[0],
        {
          key: 'submodel',
          order: sortItems[0].order,
        },
      ];
    } else {
      sortBy.value = sortItems;
    }
  }

  sortByFromFilter.value = sortBy.value;
};
</script>

<template>
  <BasePage :loading="loadingFirstTime || !filterOptions">
    <template #title>
      <BasePageTitle :title="$t('AddDosimetryAndQaEntity')" />
    </template>

    <template #actions>
      <PtwBackButton
        :to="{
          name: routenames.qaEquipment.overview,
        }"
        @click="
          () => {
            leavingPage = true;
            qaEquipmentStore.setFilterObject({});
          }
        "
      />
    </template>

    <BasePageNavigation :tabs="tabs">
      <template #ptw>
        <div class="d-flex flex-row align-center mb-4">
          <div class="d-flex flex-column">
            <p class="text-pre-line">
              {{ $t('PtwProductCatalogMsg')
              }}<InfoMessage :value="$t('PtwProductCatalogTooltipMsg')" />
            </p>
          </div>
          <v-divider
            v-if="quickLinkOptions?.length"
            class="mx-8 h-auto"
            color="primary-lighten-4"
            thickness="2"
            vertical
          ></v-divider>
          <QaEquipmentAddQuickLinks :options="quickLinkOptions" />
        </div>

        <QaEquipmentAddFilterAndSorting
          v-model:sort="sortByFromFilter"
          :items="filterOptions"
          :sortByItems="sortByItems"
          class="mb-4"
          @reset="onResetFilters"
        />
        <OverviewTable
          ref="table"
          v-model:sortBy="sortBy"
          class="mt-2"
          :headers="headers"
          :items="options"
          :loading="loading"
          :height="height"
          fixedHeader
          data-testid="DataTable_DosimetryQaEntityConfiguration"
          @update:sortBy="handleSortByChange"
        >
          <template #headers="{ columns, isSorted, getSortIcon, toggleSort }">
            <tr>
              <template v-for="column in columns" :key="column.key">
                <th
                  v-if="!column.hidden"
                  class="v-data-table__td v-data-table-column--align-start v-data-table__th--sortable v-data-table__th"
                  :style="{ width: column.width }"
                  v-bind="column.headerProps"
                  @click="() => toggleSort(column)"
                >
                  <span class="mr-2 cursor-pointer">{{ column.title }}</span>
                  <template v-if="isSorted(column)">
                    <v-icon :icon="getSortIcon(column)"></v-icon>
                  </template>
                </th>
              </template>
            </tr>
          </template>
          <template #item="{ item }">
            <tr class="v-data-table__tr v-data-table__tr--clickable" @click="clickRow(item)">
              <td>
                <IdentifierLabel :identifier="item.subModel" :label="item.model" />
              </td>
              <td>
                {{ item.brand1 ? $t(`BrandEnum.${item.brand1}` as I18nKey) : EMPTY_FIELD_SIGN }}
              </td>
              <td>
                {{ $t(`QaEquipmentSubCategoryEnum.${item.subCategory}` as I18nKey) }}
              </td>
              <td>{{ item.typeNumber }}</td>
              <td>
                {{ item.withSerialNumber === 'WithSerialNumber' ? $t('Yes') : EMPTY_FIELD_SIGN }}
              </td>
            </tr>
          </template>
        </OverviewTable>

        <QaEquipmentRegistration immediate />
      </template>
      <template #otherManufacturer>
        <h5 class="mb-2 mt-2">{{ $t('OtherManufacturer') }}</h5>
        <p class="text-pre-line mb-4">{{ $t('OtherManufacturerMsg') }}</p>
        <form
          @submit.prevent="
            router.push({
              name: routenames.qaEquipment.add.otherManufacturer,
              params: {
                subCategory: otherQaEquipmentOptionSelected,
              },
            })
          "
        >
          <v-row>
            <v-col>
              <Autocomplete
                v-model="otherQaEquipmentOptionSelected"
                :label="$t('Type_one')"
                :items="otherQaEquipmentOptions"
                :prependInnerIcon="medicalSystemTypeSubCategory"
                :placeholder="$t('Select')"
                emptyAsUndefined
              />
            </v-col>
            <v-col>
              <Select
                :value="$t('RtSystemManufacturerEnum.MS_Other')"
                disabled
                :label="$t('Manufacturer')"
                :items="[
                  {
                    title: $t('RtSystemManufacturerEnum.MS_Other'),
                    value: 'OtherManufacturer',
                  },
                ]"
                :prependInnerIcon="medicalSystemTypeManufacturer"
              />
            </v-col>
            <v-col>
              <Select
                :value="$t('RtSystemModelEnum.OtherModel')"
                disabled
                :label="$t('Model')"
                :items="[
                  {
                    title: $t('RtSystemModelEnum.OtherModel'),
                    value: 'OtherModel',
                  },
                ]"
                :prependInnerIcon="medicalSystemTypeModel"
              />
            </v-col>
            <v-col class="align-self-end">
              <SubmitButton
                :text="$t('ChooseOtherManufacturerQaEquipmentModel')"
                :disabled="!otherQaEquipmentOptionSelected"
              />
            </v-col>
          </v-row>
        </form>
      </template>
    </BasePageNavigation>
  </BasePage>
</template>

<style lang="scss" scoped>
:deep(.v-checkbox .v-selection-control) {
  min-height: initial;
}
</style>
